/**
 * Grafana Faro - Frontend Real User Monitoring
 *
 * Provides:
 * - Error tracking (JavaScript errors, unhandled rejections)
 * - Performance metrics (Core Web Vitals, page load, etc.)
 * - Distributed tracing (end-to-end request tracing)
 * - Session replay (optional)
 *
 * @see https://grafana.com/docs/grafana-cloud/frontend-observability/faro-web-sdk/
 */

import {
	getWebInstrumentations,
	initializeFaro,
	faro,
	TransportItemType,
	type ExceptionEvent,
	type TransportItem
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation, getDefaultOTELInstrumentations } from '@grafana/faro-web-tracing';
import type { Span } from '@opentelemetry/api';

// Faro collector URL — always provided by the caller (from backend config).
// No hardcoded default: initFaro() must be called with an explicit URL
// obtained from the backend /api/config endpoint (faro.collector_url).
// This ensures enterprise deployments with ENABLE_FARO=false never send
// telemetry to any collector.

// Get build-time constants with fallbacks for development
// These are injected by Vite's define config at build time
function getBuildHash(): string {
	try {
		// @ts-expect-error - APP_BUILD_HASH is injected at build time
		return typeof APP_BUILD_HASH !== 'undefined' ? APP_BUILD_HASH : 'dev';
	} catch {
		return 'dev';
	}
}

function getEnvironment(): string {
	try {
		// @ts-expect-error - BUILD_ENV is injected at build time from ENV variable
		return typeof BUILD_ENV !== 'undefined' ? BUILD_ENV : 'dev';
	} catch {
		return 'dev';
	}
}

/**
 * Faro beforeSend hook: drop known-benign errors before they reach the collector.
 *
 * Categories filtered:
 * 1. Browser extension errors — stack frames originating from chrome-extension:// or
 *    moz-extension:// are third-party code; we have no control over them.
 * 2. OTel instrumentation-fetch crashes — Faro's own OpenTelemetry fetch wrapper can
 *    crash with "ReadableStreamDefaultController" when it receives HTML (e.g. a 502
 *    from Cloudflare) instead of JSON.  These are SDK-internal issues, not app bugs.
 * 3. MetaMask extension errors — "Failed to connect to MetaMask" is emitted by the
 *    MetaMask browser extension inpage script, not our application.
 * 4. Facebook/iOS In-App Browser bridge errors — the FBAN/FBIOS in-app browser
 *    injects webkit.messageHandlers bridge code that can crash when handlers
 *    aren't registered. Not our application code.
 * 5. Media playback errors — NotSupportedError ("Failed to load because no supported
 *    source was found") and AbortError ("The play() request was interrupted") are
 *    benign browser errors from codec mismatches or interrupted play requests.
 * 6. Firefox/cross-browser JSON parse errors from OTel fetch instrumentation — Firefox
 *    formats JSON parse errors as "JSON.parse: unexpected character at line N column N"
 *    instead of Chrome's "Unexpected token…" format, but both originate from the same
 *    root cause: OTel receiving non-JSON (e.g. HTML 502) where it expects JSON.
 * 7. URI malformed — when goto() is called with a redirect path containing invalid
 *    percent-encoding (e.g. ?redirect=%YXABVLCC from bots or misconfigured links),
 *    SvelteKit's internal decodeURI() on the path throws. Not an application bug.
 * 8. Svelte transition race conditions — InvalidStateError thrown by the browser's Web
 *    Animations API when a CSS transition is aborted due to conflicting DOM state changes
 *    (e.g. rapid navigation, tab switch while a transition is running). Non-actionable;
 *    the browser handles this gracefully and the transition simply doesn't complete.
 */
function faroBeforeSend(item: TransportItem): TransportItem | null {
	// Drop trace batches where every span exceeds 60s (tab suspension artifacts).
	// These are caused by backgrounded browser tabs freezing fetch requests while
	// OTel records wall-clock time as span duration. Only drops the batch when ALL
	// spans are frozen — if any span has a reasonable duration, the batch is kept.
	if (item.type === TransportItemType.TRACE) {
		const payload = item.payload as {
			spans?: Array<{ startTimeUnixNano?: string; endTimeUnixNano?: string }>;
		};
		const spans = payload?.spans;
		if (spans && spans.length > 0) {
			const hasLegitimateSpan = spans.some((span) => {
				try {
					if (!span.startTimeUnixNano || !span.endTimeUnixNano) return true;
					const startNs = BigInt(span.startTimeUnixNano);
					const endNs = BigInt(span.endTimeUnixNano);
					const durationMs = Number((endNs - startNs) / BigInt(1_000_000));
					return durationMs <= MAX_REASONABLE_SPAN_MS;
				} catch {
					return true; // Parse failure — treat as legitimate to avoid dropping data
				}
			});
			if (!hasLegitimateSpan) {
				return null;
			}
		}
		return item;
	}

	if (item.type !== TransportItemType.EXCEPTION) {
		return item;
	}

	const exception = item.payload as ExceptionEvent;
	const message: string = exception?.value ?? '';
	const frames = exception?.stacktrace?.frames ?? [];
	const stackText = frames.map((f) => f?.filename ?? '').join('\n');

	// 1. Browser extension errors
	if (stackText.includes('chrome-extension://') || stackText.includes('moz-extension://')) {
		return null;
	}

	// 2. OTel instrumentation-fetch crashes on non-JSON responses.
	// The fetch instrumentation tries to clone+parse the response body and
	// throws "Failed to execute 'close' on 'ReadableStreamDefaultController'"
	// when an HTTP error returns HTML (e.g. <!DOCTYPE) instead of JSON.
	// The stack-frame check originally guarded this but we still saw a few
	// leak through — the parsed `frames` array is sometimes empty in the
	// Exception payload, so the stackText fallback misses them.  The message
	// itself is unique enough (no legitimate app code closes a stream
	// controller this way) that matching on it alone is safe.
	if (message.includes("'close' on 'ReadableStreamDefaultController'")) {
		return null;
	}

	// 3. MetaMask extension errors
	if (message.includes('Failed to connect to MetaMask')) {
		return null;
	}

	// 4. Facebook/iOS In-App Browser bridge errors
	// Facebook's FBIOS in-app browser injects a WKWebView JS bridge that crashes
	// when webkit.messageHandlers aren't registered. Not our application code.
	if (message.includes('webkit.messageHandlers')) {
		return null;
	}

	// 5. Media playback errors — benign browser errors when a codec is unsupported
	// or when a play() request is interrupted by a subsequent src change / pause.
	// These surface as NotSupportedError / AbortError and are not application bugs.
	if (
		message.includes('Failed to load because no supported source was found') ||
		message.includes('The play() request was interrupted')
	) {
		return null;
	}

	// 6. Firefox JSON.parse errors from OTel instrumentation — Firefox formats JSON
	// parse errors as "JSON.parse: unexpected character at line N column N..." instead
	// of Chrome's "Unexpected token..." format. Same root cause as #2 above.
	if (
		message.startsWith('JSON.parse:') &&
		(stackText.includes('instrumentation-fetch') || stackText.includes('@opentelemetry'))
	) {
		return null;
	}

	// 7. Generic JSON parse errors from OTel instrumentation — catches any browser's
	// variant of SyntaxError from JSON.parse when OTel fetch wrapper receives non-JSON.
	// 'unexpected character' = Firefox; 'Unexpected token' / 'is not valid JSON' = Chrome/V8.
	if (
		(message.includes('unexpected character') ||
			message.includes('Unexpected token') ||
			message.includes('is not valid JSON')) &&
		(stackText.includes('instrumentation-fetch') || stackText.includes('@opentelemetry'))
	) {
		return null;
	}

	// 7. URI malformed — when the ?redirect= query parameter contains invalid
	// percent-encoding (e.g. %YXABVLCC from a bot or misconfigured external link),
	// calling goto() with the value causes SvelteKit's internal `decodeURI()` on
	// the path to throw "URI malformed". The app-level getSafeRedirect() guard
	// in +page.svelte drops the bad value before goto() is reached, but this
	// filter catches any remaining edge-case occurrences to avoid Faro noise.
	// Stack patterns validated against @sveltejs/kit 2.x (url.js:51, client.js:1448).
	if (
		exception?.type === 'URIError' &&
		message.includes('URI malformed') &&
		(stackText.includes('decodeURI') || stackText.includes('@sveltejs/kit'))
	) {
		return null;
	}

	// 8. Svelte transition race conditions — the browser's Web Animations API throws
	// InvalidStateError when a CSS transition/animation is aborted due to conflicting
	// DOM state changes (rapid navigation, tab switch while a transition is running,
	// or a component being destroyed while its entry/exit transition is still active).
	// This is non-actionable: the browser handles it gracefully and the transition
	// simply does not complete. No user-visible impact.
	if (
		exception?.type === 'InvalidStateError' &&
		message.includes('Transition was aborted because of invalid state')
	) {
		return null;
	}

	// 9. ProseMirror Selection.collapse() race condition on mobile (IndexSizeError)
	if (
		exception?.type === 'IndexSizeError' &&
		(stackText.includes('prosemirror') || stackText.includes('tiptap'))
	) {
		return null;
	}

	// 10. ProseMirror internal crash — "nodeSize" errors originating from prosemirror frames
	if (message.includes('nodeSize') && stackText.includes('prosemirror')) {
		return null;
	}

	// 11. OTel fetch instrumentation noise — readable stream reader released variant
	if (
		message.includes('ReadableStream reader was released') &&
		(stackText.includes('instrumentation-fetch') || stackText.includes('@opentelemetry'))
	) {
		return null;
	}

	// 12. TipTap crash on Chrome <97 — Array.prototype.findLast not available (tracked in #451)
	if (
		message.includes('findLast is not a function') &&
		(stackText.includes('tiptap') || stackText.includes('prosemirror'))
	) {
		return null;
	}

	return item;
}

// Maximum reasonable span duration (60s). Trace batches where ALL spans exceed
// this threshold are dropped in faroBeforeSend as tab suspension artifacts.
const MAX_REASONABLE_SPAN_MS = 60_000;

/**
 * Custom fetch span attributes for better observability.
 *
 * Enriches fetch spans with:
 * - PerformanceResourceTiming actual duration (for detecting freeze artifacts)
 * - Stream disconnect detection (http.status_code=0 on failed SSE streams)
 */
function applyFetchSpanAttributes(
	span: Span,
	request: Request | RequestInit,
	result: Response | { status?: number; message: string }
): void {
	try {
		// Enrich with PerformanceResourceTiming actual network duration
		const url = request instanceof Request ? request.url : (request as { url?: string }).url;
		if (url && typeof performance !== 'undefined' && performance.getEntriesByName) {
			const entries = performance.getEntriesByName(url, 'resource');
			if (entries.length > 0) {
				const entry = entries[entries.length - 1] as PerformanceResourceTiming;
				span.setAttribute('http.actual_duration_ms', Math.round(entry.duration));
			}
		}

		// Tag stream disconnects: when a streaming response drops mid-stream,
		// the browser reports status=0 / network error. These inflate p95 with
		// the full wait time rather than actual server latency.
		if (result instanceof Response) {
			if (result.status === 0) {
				span.setAttribute('http.stream_disconnect', true);
			}
		} else if ('message' in result) {
			// FetchError (not a Response) — network-level failure
			span.setAttribute('http.stream_disconnect', true);
		}
	} catch {
		// Never let telemetry enrichment break the request
	}
}

let faroInitialized = false;

/**
 * Initialize Grafana Faro for frontend observability
 *
 * Only initializes in production to avoid CORS issues in development.
 * Should be called early in app lifecycle (e.g., hooks.client.js)
 */
export function initFaro(collectorUrl?: string): void {
	// Only initialize once
	if (faroInitialized) {
		return;
	}

	// Skip in SSR context
	if (typeof window === 'undefined') {
		return;
	}

	const environment = getEnvironment();

	// Skip in development - Grafana Cloud collector has CORS restrictions
	if (environment === 'dev') {
		console.debug('[Faro] Skipped initialization in development mode');
		return;
	}

	// Collector URL is required — must be provided by the caller from backend config.
	if (!collectorUrl) {
		console.debug('[Faro] No collector URL provided, skipping initialization');
		return;
	}

	try {
		initializeFaro({
			url: collectorUrl,
			app: {
				name: 'cochat',
				version: getBuildHash(),
				environment: environment
			},
			batching: {
				enabled: true,
				sendTimeout: 2000,
				itemLimit: 100
			},
			beforeSend: faroBeforeSend,
			instrumentations: [
				// Default web instrumentations (errors, console, performance, etc.)
				...getWebInstrumentations({
					captureConsole: false // Don't capture console.log - too noisy
				}),
				// Tracing with PerformanceResourceTiming enrichment, stream disconnect tagging,
				// and ignoreUrls for third-party domains that pollute trace data.
				// Frozen spans (>60s from tab suspension) are dropped in faroBeforeSend.
				new TracingInstrumentation({
					instrumentations: getDefaultOTELInstrumentations({
						ignoreUrls: [
							collectorUrl,
							/google-analytics\.com/,
							/googletagmanager\.com/,
							/analytics\.google\.com/,
							/posthog\.com/,
							/fonts\.googleapis\.com/,
							/fonts\.gstatic\.com/
						],
						fetchInstrumentationOptions: {
							applyCustomAttributesOnSpan: applyFetchSpanAttributes
						}
					})
				})
			]
		});

		faroInitialized = true;
		console.debug('[Faro] Initialized successfully');
	} catch (error) {
		console.error('[Faro] Failed to initialize:', error);
	}
}

/**
 * Set user context for Faro (call after authentication)
 */
export function setFaroUser(user: { id: string; email?: string; name?: string }): void {
	if (!faroInitialized || !faro.api) {
		return;
	}

	try {
		faro.api.setUser({
			id: user.id,
			email: user.email,
			username: user.name
		});
		console.debug('[Faro] User context set');
	} catch (error) {
		console.error('[Faro] Failed to set user:', error);
	}
}

/**
 * Clear user context (call on logout)
 */
export function clearFaroUser(): void {
	if (!faroInitialized || !faro.api) {
		return;
	}

	try {
		faro.api.resetUser();
		console.debug('[Faro] User context cleared');
	} catch (error) {
		console.error('[Faro] Failed to clear user:', error);
	}
}

/**
 * Push a custom event to Faro
 */
export function pushFaroEvent(name: string, attributes?: Record<string, string>): void {
	if (!faroInitialized || !faro.api) {
		return;
	}

	try {
		faro.api.pushEvent(name, attributes);
	} catch (error) {
		console.error('[Faro] Failed to push event:', error);
	}
}
